<template>
  <div>
    <div class="survey">
      <div
        v-if="survey === null"
        class="survey__loader"
      >
        <b-spinner variant="primary" label="Loading"></b-spinner>
      </div>

      <div v-else>
        <div
          v-if="finished"
          class="survey__finish"
          v-html="survey.end_text"
        />

        <ValidationObserver
          slim
          v-slot="{ failed }"
          ref="observer"
          v-else
        >
          <b-form @submit.prevent="onSubmit">
            <div
              v-for="({
                _id: questionId,
                type: questionType,
                content: questionContent,
                required: questionRequired,
              }) in surveyQuestions"
              :key="`question-${questionId}`"
              class="survey__question"
            >
              <div
                v-html="questionContent.question_text"
                class="survey__question-text"
              />

              <div class="survey__question-body">
                <template v-if="questionType === 'text'">
                  <ValidationProvider
                    :rules="getQuestionRules(questionRequired)"
                    v-slot="{ errors }"
                  >
                    <b-form-textarea
                      rows="3"
                      max-rows="6"
                      v-model="getAnswerObjectById(questionId).text"
                    ></b-form-textarea>

                    <div class="survey__field-error">{{ errors[0] }}</div>
                  </ValidationProvider>
                </template>
                
                <template v-if="questionType === 'options'">
                  <ValidationProvider
                    :rules="getQuestionRules(questionRequired)"
                    v-slot="{ errors }"
                  >
                    <template v-if="questionContent.multichoice">
                      <b-form-checkbox-group
                        v-model="getAnswerObjectById(questionId).options"
                        :name="`question-${questionId}`"
                      >
                        <b-form-checkbox
                          v-for="option in questionContent.options"
                          :key="`question-${questionId}__option-${option._id}`"
                          :value="option.text"
                        >
                          <span v-html="option.text" />
                        </b-form-checkbox>
                      </b-form-checkbox-group>
                    </template>

                    <template v-else>
                      <b-form-radio-group
                        v-model="getAnswerObjectById(questionId).options"
                        :name="`question-${questionId}`"
                      >
                        <b-form-radio
                          v-for="option in questionContent.options"
                          :key="`question-${questionId}__option-${option._id}`"
                          :value="[option.text]"
                        >
                          <span v-html="option.text" />
                        </b-form-radio>
                      </b-form-radio-group>
                    </template>

                    <div class="survey__field-error">{{ errors[0] }}</div>
                  </ValidationProvider>

                  <template v-if="questionContent.comment && questionContent.comment.enabled">
                    <b-form-textarea
                      rows="3"
                      max-rows="6"
                      v-model="getAnswerObjectById(questionId).text"
                    />
                  </template>
                </template>

                <template v-if="questionType === 'stars5'">
                  <ValidationProvider
                    :rules="getQuestionRules(questionRequired)"
                    v-slot="{ errors }"
                  >
                    <b-form-radio-group
                      v-model="getAnswerObjectById(questionId).stars"
                      :name="`question-${questionId}`"
                    >
                      <b-form-radio
                        v-for="star in [1,2,3,4,5]"
                        :key="`question-${questionId}__star-${star}`"
                        :value="star"
                        class="survey__star"
                        :class="{
                          '--state-hovered': star <= getAnswerObjectById(questionId).stars,
                        }"
                      >
                        <font-awesome-icon icon="star" />
                      </b-form-radio>
                    </b-form-radio-group>

                    <div class="survey__field-error">{{ errors[0] }}</div>
                  </ValidationProvider>
                </template>

                <template v-if="questionType === 'grades10'">
                  <ValidationProvider
                    :rules="getQuestionRules(questionRequired)"
                    v-slot="{ errors }"
                  >
                    <b-form-radio-group
                      v-model="getAnswerObjectById(questionId).grade"
                      :name="`question-${questionId}`"
                      class="survey__nps"
                    >
                      <b-form-radio
                        v-for="grade in [0,1,2,3,4,5,6,7,8,9,10]"
                        :key="`question-${questionId}__grade-${grade}`"
                        :value="grade"
                        class="survey__nps-number"
                        :class="{
                          '--state-selected': grade === getAnswerObjectById(questionId).grade,
                        }"
                      >
                        {{ grade }}
                      </b-form-radio>
                    </b-form-radio-group>

                    <div class="survey__nps-hints">
                      <span>{{ questionContent.hints && questionContent.hints.left }}</span>
                      <span>{{ questionContent.hints && questionContent.hints.right }}</span>
                    </div>

                    <div class="survey__field-error">{{ errors[0] }}</div>
                  </ValidationProvider>
                </template>

                <template v-if="questionType === 'statement'">
                  <ValidationProvider
                    :rules="getQuestionRules(questionRequired)"
                    v-slot="{ errors }"
                  >
                    <b-form-radio-group
                      v-model="getAnswerObjectById(questionId).statement"
                      :name="`question-${questionId}`"
                      class="survey__statement"
                    >
                      <b-form-radio
                        v-for="statement in ['good', 'bad']"
                        :key="`question-${questionId}__statement-${statement}`"
                        :value="statement"
                        class="survey__statement-item"
                        :class="{
                          '--state-selected': statement === getAnswerObjectById(questionId).statement,
                        }"
                      >
                        <font-awesome-icon :icon="statement === 'good' ? 'thumbs-up' : 'thumbs-down'" />
                      </b-form-radio>
                    </b-form-radio-group>

                    <div class="survey__field-error">{{ errors[0] }}</div>
                  </ValidationProvider>
                </template>

                <template v-if="questionType === 'date'">
                  <ValidationProvider
                    :rules="getQuestionRules(questionRequired)"
                    v-slot="{ errors }"
                  >
                    <b-form-input
                      v-model="getAnswerObjectById(questionId).date"
                      type="date"
                    />

                    <div class="survey__field-error">{{ errors[0] }}</div>
                  </ValidationProvider>
                </template>

                <template v-if="questionType === 'email'">
                  <ValidationProvider
                    :rules="getQuestionRules(questionRequired)"
                    v-slot="{ errors }"
                  >
                    <b-form-input
                      v-model="getAnswerObjectById(questionId).text"
                      type="email"
                    />

                    <div class="survey__field-error">{{ errors[0] }}</div>
                  </ValidationProvider>
                </template>

                <template v-if="questionType === 'phone'">
                  <ValidationProvider
                    :rules="getQuestionRules(questionRequired)"
                    v-slot="{ errors }"
                  >
                    <b-form-input
                      v-model="getAnswerObjectById(questionId).text"
                      type="tel"
                    />

                    <div class="survey__field-error">{{ errors[0] }}</div>
                  </ValidationProvider>
                </template>

                <template v-if="questionType === 'emotions'">
                  <ValidationProvider
                    :rules="getQuestionRules(questionRequired)"
                    v-slot="{ errors }"
                  >
                    <b-form-radio-group
                      v-model="getAnswerObjectById(questionId).emotion"
                      :name="`question-${questionId}`"
                      class="survey__emotions"
                    >
                      <b-form-radio
                        v-for="emotion in [1,2,3,4,5]"
                        :key="`question-${questionId}__emotion-${emotion}`"
                        :value="emotion"
                        class="survey__emotions-item"
                        :class="{
                          '--state-selected': emotion === getAnswerObjectById(questionId).emotion,
                          '--state-muted':  getAnswerObjectById(questionId).emotion && emotion !== getAnswerObjectById(questionId).emotion,
                        }"
                      >
                        <img :src="`/images/emotions/${emotion}.svg`" />
                      </b-form-radio>
                    </b-form-radio-group>

                    <div class="survey__field-error">{{ errors[0] }}</div>
                  </ValidationProvider>
                </template>
              </div>
            </div>

            <b-form-group>
              <div class="survey__invalid" v-if="failed">
                {{ $t('common.missingRequiredFields') }}
              </div>

              <b-button
                type="submit"
                variant="primary"
                :disabled="failed"
              >
                <b-spinner
                  v-if="sending"
                  small
                />

                <template v-else>
                  {{ $t('common.send') }}
                </template>
              </b-button>
            </b-form-group>
          </b-form>
        </ValidationObserver>
      </div>
    </div>
  </div>
</template>

<script>
import { find } from 'lodash';
import { answerObject, checkQuestionLogic } from '../../helpers';

export default {
  props: {
    tabletId: {
      type: String,
    },

    tabletLink: {
      type: String,
    },
  },

  data() {
    return {
      survey: null,
      answer: null,

      finished: false,
      sending: false,
    }
  },

  computed: {
    surveyQuestions() {
      return this.survey.questions.filter(({ logic }) => {
        if (!logic || checkQuestionLogic(logic, this.answer)) {
          return true;
        }
      });
    },

    gaLabel() {
      return this.tabletLink ? `link:${this.tabletLink}` : `id:${this.tabletLink}`;
    },
  },

  mounted() {
    this.setSurvey();  
  },

  methods: {
    getQuestionRules(questionRequired) {
      return questionRequired ? 'required' : '';
    },

    addExtraStyles(styles) {
      var sheet = document.createElement('style');
      sheet.innerHTML = styles;
      document.body.appendChild(sheet);
    },

    async setSurvey() {
      try {
        const { tabletId, tabletLink } = this;

        const { survey, tablet } = await this.$ddp.call('links.getSurvey', {
          tabletId,
          tabletLink,
        });
  
        this.answer = answerObject(survey, tablet._id);
        this.survey = survey;

        if (survey.css_styles_web) {
          this.addExtraStyles(survey.css_styles_web);
        }

        gtag('event', 'open', {
          event_category: 'Survey',
          event_label: this.gaLabel,
          value: 1,
        });

      } catch (error) {
        console.error(error);
      }
    },

    getAnswerObjectById(_id) {
      const question = find(this.answer.questions, { _id });
      return question && question.answer;
    },

    async onSubmit() {
      try {
        const validation = await this.$refs.observer.validate();

        if (validation) {
          this.sending = true;
          this.answer.leftAt = new Date();
          await this.$ddp.call('answers.insert', [ this.answer ]);
          this.finished = true;

          gtag('event', 'submit', {
            event_category: 'Survey',
            event_label: this.gaLabel,
            value: 10,
          });
        }

      } catch (error) {
        console.error(error);
      } finally {
        this.sending = false;
      }
    },
  },
}
</script>

<style
  lang="scss"
  src="./Survey.scss"
/>